import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CASH_PAY_PRODUCT_TYPES,
  CONSENT_DOCUMENT_TYPES,
  INJECTABLE_PRODUCT_CLASSES,
  PAYMENT_METHODS,
  PHOTO_ANGLES,
  PHOTO_STANDARDIZATION_GUIDANCE,
  RETAIL_PAYMENT_METHODS,
  consentDocumentTypeLabel,
  decide,
  groupPhotosIntoTimeline,
  injectableProductClassLabel,
  isRevocableConsentType,
  photoAngleLabel,
  requiredConsentTypeForProductClass,
  tracksUnitBalance,
  type BodyMapRegion,
  type BodyMapSide,
  type CashPayProductResponse,
  type CashPayPurchaseResponse,
  type ConsentDocumentResponse,
  type InjectableAdministrationResponse,
  type InjectableProductClass,
  type PatientPhotoResponse,
  type RetailSaleResponse,
} from '@emr/shared';

import {
  createCashPayProduct,
  createCashPayPurchase,
  createConsentDocument,
  createInjectableAdministration,
  createPatientPhoto,
  createRetailSale,
  fetchCashPayProducts,
  fetchCashPayPurchases,
  fetchConsentDocuments,
  fetchInjectableAdministrations,
  fetchPatientPhotos,
  fetchRetailSales,
  redeemCashPayUnits,
  revokeConsentDocument,
} from '../api/aesthetics.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { BodyMapPicker } from '../components/BodyMapPicker.tsx';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';

/**
 * The aesthetics/med-spa module's real, patient-scoped surface (`BACKLOG.md` M9). Reachable directly
 * by URL even with the catalog entry showing an upgrade prompt for an unlicensed org, matching
 * `PatientDermatologyPage.tsx`'s own stated reasoning for re-applying `GatedRoute` here.
 *
 * Five sections, each independently queried and independently writable behind `chart:write` (the
 * three clinical ones) or `billing:manage` (the two billing/retail ones — `aesthetics/routes.ts`'s
 * own header explains the split):
 *
 *   - **Consent documents** — the three distinct concepts (photograph, marketing/social-media-use,
 *     per-product-class treatment consent), plus revoke for the ordinarily-revocable ones.
 *   - **Photo documentation** — grouped into a same-day timeline via `@emr/shared`'s
 *     `groupPhotosIntoTimeline`, gated on a real photograph consent existing first.
 *   - **Injectable charting** — product/lot/units/site, gated on the matching per-product-class
 *     consent, with lot number effectively mandatory.
 *   - **Cash-pay billing** — the practice's own product catalog plus this patient's purchases and
 *     unit redemption, genuinely separate from insurance RCM (no eligibility/claim/EOB anywhere).
 *   - **Retail POS** — a narrow, chart-linked sale record; the total is always server-computed.
 */
export function PatientAestheticsPage() {
  const { t } = useTranslation('aesthetics');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('pageTitle')} capability="chart:read" feature="aestheticsModule">
      <div className="flex flex-col gap-6">
        <ConsentSection patientId={patientId} />
        <PhotoSection patientId={patientId} />
        <InjectableSection patientId={patientId} />
        <CashPaySection patientId={patientId} />
        <RetailSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

// --- Consent documents ----------------------------------------------------------------------------

function ConsentSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('aesthetics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['aesthetics', 'consents', patientId];

  const query = useQuery<readonly ConsentDocumentResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchConsentDocuments(patientId, signal),
  });

  const [consentType, setConsentType] =
    useState<(typeof CONSENT_DOCUMENT_TYPES)[number]>('photograph');
  const [signedOn, setSignedOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createConsentDocument(patientId, {
        consentType,
        signedOn,
        method: 'in_person',
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setSignedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const revoke = useMutation({
    mutationFn: (consentId: string) => revokeConsentDocument(patientId, consentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('consent.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('consent.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('consent.emptyMessage')}
        >
          {(consents) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {consents.map((consent) => (
                <li
                  key={consent.id}
                  className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="m-0 font-medium text-ink">
                      {consentDocumentTypeLabel(consent.consentType)}
                    </p>
                    <p className="m-0 text-ink-muted">
                      {t('consent.signedOn', { date: consent.signedOn })}
                      {consent.revokedAt !== null ? ` ${t('consent.revokedSuffix')}` : ''}
                    </p>
                  </div>
                  {mayWrite &&
                  consent.revokedAt === null &&
                  isRevocableConsentType(
                    consent.consentType as (typeof CONSENT_DOCUMENT_TYPES)[number],
                  ) ? (
                    <Button
                      variant="secondary"
                      pending={revoke.isPending}
                      onClick={() => {
                        revoke.mutate(consent.id);
                      }}
                    >
                      {t('consent.revokeButton')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('consent.newTypeLabel')}</span>
              <select
                value={consentType}
                onChange={(event) => {
                  setConsentType(event.target.value as (typeof CONSENT_DOCUMENT_TYPES)[number]);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {CONSENT_DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {consentDocumentTypeLabel(type)}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('consent.signedOnFieldLabel')}
              type="date"
              value={signedOn}
              onChange={(event) => {
                setSignedOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('consent.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('consent.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Photo documentation ---------------------------------------------------------------------------

function PhotoSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('aesthetics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const photosQueryKey = ['aesthetics', 'photos', patientId];
  const consentsQueryKey = ['aesthetics', 'consents', patientId];

  const photosQuery = useQuery<readonly PatientPhotoResponse[]>({
    queryKey: photosQueryKey,
    queryFn: ({ signal }) => fetchPatientPhotos(patientId, signal),
  });
  const consentsQuery = useQuery<readonly ConsentDocumentResponse[]>({
    queryKey: consentsQueryKey,
    queryFn: ({ signal }) => fetchConsentDocuments(patientId, signal),
  });

  const photographConsents = (consentsQuery.data ?? []).filter(
    (consent) => consent.consentType === 'photograph' && consent.revokedAt === null,
  );

  const [angle, setAngle] = useState<(typeof PHOTO_ANGLES)[number]>('frontal');
  const [storageRef, setStorageRef] = useState('');
  const [photographConsentId, setPhotographConsentId] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createPatientPhoto(patientId, {
        angle,
        takenAt: new Date().toISOString(),
        storageRef,
        lightingStandardized: false,
        photographConsentId,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setStorageRef('');
      void queryClient.invalidateQueries({ queryKey: photosQueryKey });
    },
  });

  const timeline = groupPhotosIntoTimeline(photosQuery.data ?? []);

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('photos.heading')}</SectionHeading>
      <details className="text-sm text-ink-muted">
        <summary className="cursor-pointer text-ink">{t('photos.guidanceSummary')}</summary>
        <ul className="m-0 mt-2 flex list-disc flex-col gap-1 pl-5">
          {PHOTO_STANDARDIZATION_GUIDANCE.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
      <Panel>
        <QuerySection
          query={photosQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('photos.emptyMessage')}
        >
          {() => (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {timeline.map((entry) => (
                <li key={entry.date} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">{entry.date}</p>
                  <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                    {entry.photos.map((photo) => (
                      <li key={photo.id}>{photoAngleLabel(photo.angle)}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          photographConsents.length === 0 ? (
            <p className="m-0 mt-4 text-sm text-ink-muted">{t('photos.noConsentMessage')}</p>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate();
              }}
              className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
            >
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('photos.angleLabel')}</span>
                <select
                  value={angle}
                  onChange={(event) => {
                    setAngle(event.target.value as (typeof PHOTO_ANGLES)[number]);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {PHOTO_ANGLES.map((value) => (
                    <option key={value} value={value}>
                      {photoAngleLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label={t('photos.storageRefLabel')}
                value={storageRef}
                onChange={(event) => {
                  setStorageRef(event.target.value);
                }}
                placeholder={t('photos.storageRefPlaceholder')}
                required
                className="sm:max-w-[24ch]"
              />
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('photos.consentLabel')}</span>
                <select
                  value={photographConsentId}
                  onChange={(event) => {
                    setPhotographConsentId(event.target.value);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  <option value="">{t('common.select')}</option>
                  {photographConsents.map((consent) => (
                    <option key={consent.id} value={consent.id}>
                      {consent.signedOn}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="submit"
                pending={create.isPending}
                disabled={photographConsentId === ''}
              >
                {t('photos.addButton')}
              </Button>
            </form>
          )
        ) : null}
        {create.isError ? <ErrorText message={t('photos.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Injectable charting ---------------------------------------------------------------------------

function InjectableSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('aesthetics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const administrationsQueryKey = ['aesthetics', 'injectables', patientId];
  const consentsQueryKey = ['aesthetics', 'consents', patientId];

  const administrationsQuery = useQuery<readonly InjectableAdministrationResponse[]>({
    queryKey: administrationsQueryKey,
    queryFn: ({ signal }) => fetchInjectableAdministrations(patientId, signal),
  });
  const consentsQuery = useQuery<readonly ConsentDocumentResponse[]>({
    queryKey: consentsQueryKey,
    queryFn: ({ signal }) => fetchConsentDocuments(patientId, signal),
  });

  const [productName, setProductName] = useState('');
  const [productClass, setProductClass] = useState<InjectableProductClass>('neuromodulator');
  const [lotNumber, setLotNumber] = useState('');
  const [lotUnavailableReason, setLotUnavailableReason] = useState('');
  const [units, setUnits] = useState('');
  const [bodyMapRegion, setBodyMapRegion] = useState<BodyMapRegion>('glabella');
  const [bodyMapSide, setBodyMapSide] = useState<BodyMapSide | null>(null);
  const [administeredOn, setAdministeredOn] = useState('');
  const [consentDocumentId, setConsentDocumentId] = useState('');

  const requiredType = requiredConsentTypeForProductClass(productClass) ?? 'treatment_general';
  const matchingConsents = (consentsQuery.data ?? []).filter(
    (consent) => consent.consentType === requiredType && consent.revokedAt === null,
  );

  const create = useMutation({
    mutationFn: () =>
      createInjectableAdministration(patientId, {
        productName,
        productClass,
        lotNumber: lotNumber === '' ? undefined : lotNumber,
        lotNumberUnavailableReason: lotUnavailableReason === '' ? undefined : lotUnavailableReason,
        units,
        bodyMapRegion,
        bodyMapSide: bodyMapSide === null ? undefined : bodyMapSide,
        administeredOn,
        consentDocumentId,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setProductName('');
      setLotNumber('');
      setLotUnavailableReason('');
      setUnits('');
      setAdministeredOn('');
      void queryClient.invalidateQueries({ queryKey: administrationsQueryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('injectables.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('injectables.description')}</p>
      <Panel>
        <QuerySection
          query={administrationsQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('injectables.emptyMessage')}
        >
          {(administrations) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {administrations.map((administration) => (
                <li
                  key={administration.id}
                  className="rounded-control border border-line p-3 text-sm"
                >
                  <p className="m-0 font-medium break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
                    {administration.productName} (
                    {injectableProductClassLabel(administration.productClass)}) &mdash;{' '}
                    {administration.units} {t('injectables.unitsWord')}
                  </p>
                  <p className="m-0 break-words whitespace-normal text-ink-muted [overflow-wrap:anywhere]">
                    {t(`vocab.bodyMapRegions.${administration.bodyMapRegion}`)}
                    {administration.bodyMapSide !== null
                      ? ` (${t(`vocab.bodyMapSides.${administration.bodyMapSide}`)})`
                      : ''}
                    {' — '}
                    {administration.administeredOn}
                    {administration.lotNumber !== null
                      ? ` ${t('injectables.lotSuffix', { lot: administration.lotNumber })}`
                      : ` ${t('injectables.lotUnavailableSuffix', { reason: administration.lotNumberUnavailableReason ?? '' })}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('injectables.productNameLabel')}
              value={productName}
              onChange={(event) => {
                setProductName(event.target.value);
              }}
              placeholder={t('injectables.productNamePlaceholder')}
              required
              className="sm:max-w-[18ch]"
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('injectables.productClassLabel')}
              </span>
              <select
                value={productClass}
                onChange={(event) => {
                  setProductClass(event.target.value as InjectableProductClass);
                  setConsentDocumentId('');
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {INJECTABLE_PRODUCT_CLASSES.map((value) => (
                  <option key={value} value={value}>
                    {injectableProductClassLabel(value)}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('injectables.lotNumberLabel')}
              value={lotNumber}
              onChange={(event) => {
                setLotNumber(event.target.value);
              }}
              className="sm:max-w-[14ch]"
            />
            <Field
              label={t('injectables.lotUnavailableReasonLabel')}
              value={lotUnavailableReason}
              onChange={(event) => {
                setLotUnavailableReason(event.target.value);
              }}
              hint={t('injectables.lotUnavailableReasonHint')}
              className="sm:max-w-[18ch]"
            />
            <Field
              label={t('injectables.unitsFieldLabel')}
              value={units}
              onChange={(event) => {
                setUnits(event.target.value);
              }}
              placeholder={t('injectables.unitsPlaceholder')}
              required
              className="sm:max-w-[10ch]"
            />
            <div className="w-full sm:w-auto">
              <BodyMapPicker
                region={bodyMapRegion}
                side={bodyMapSide}
                onChange={(next) => {
                  setBodyMapRegion(next.region);
                  setBodyMapSide(next.side);
                }}
                groupLabel={t('injectables.siteRegionLabel')}
                regionLabel={(region) => t(`vocab.bodyMapRegions.${region}`)}
                sideLabel={(side) => t(`vocab.bodyMapSides.${side}`)}
                notApplicableLabel={t('common.notApplicable')}
              />
            </div>
            <Field
              label={t('injectables.administeredOnLabel')}
              type="date"
              value={administeredOn}
              onChange={(event) => {
                setAdministeredOn(event.target.value);
              }}
              required
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('injectables.matchingConsentLabel')}
              </span>
              <select
                value={consentDocumentId}
                onChange={(event) => {
                  setConsentDocumentId(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                <option value="">{t('common.select')}</option>
                {matchingConsents.map((consent) => (
                  <option key={consent.id} value={consent.id}>
                    {consent.signedOn}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="submit"
              pending={create.isPending}
              disabled={
                consentDocumentId === '' || (lotNumber === '' && lotUnavailableReason === '')
              }
            >
              {t('injectables.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('injectables.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Cash-pay billing -----------------------------------------------------------------------------

function CashPaySection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('aesthetics');
  const grants = useGrants();
  const mayManageBilling = decide(grants, 'billing:manage');
  const queryClient = useQueryClient();
  const productsQueryKey = ['aesthetics', 'cash-pay-products'];
  const purchasesQueryKey = ['aesthetics', 'cash-pay-purchases', patientId];

  const productsQuery = useQuery<readonly CashPayProductResponse[]>({
    queryKey: productsQueryKey,
    queryFn: ({ signal }) => fetchCashPayProducts(signal),
  });
  const purchasesQuery = useQuery<readonly CashPayPurchaseResponse[]>({
    queryKey: purchasesQueryKey,
    queryFn: ({ signal }) => fetchCashPayPurchases(patientId, signal),
  });

  const [newProductName, setNewProductName] = useState('');
  const [newProductType, setNewProductType] =
    useState<(typeof CASH_PAY_PRODUCT_TYPES)[number]>('unit_bank');
  const [newPriceDollars, setNewPriceDollars] = useState('');
  const [newUnitsIncluded, setNewUnitsIncluded] = useState('');

  const createProduct = useMutation({
    mutationFn: () =>
      createCashPayProduct({
        name: newProductName,
        productType: newProductType,
        priceCents: Math.round(Number(newPriceDollars) * 100),
        unitsIncluded:
          tracksUnitBalance(newProductType) && newUnitsIncluded !== ''
            ? newUnitsIncluded
            : undefined,
      }),
    onSuccess: () => {
      setNewProductName('');
      setNewPriceDollars('');
      setNewUnitsIncluded('');
      void queryClient.invalidateQueries({ queryKey: productsQueryKey });
    },
  });

  const [productId, setProductId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<(typeof PAYMENT_METHODS)[number]>('card');
  const [purchasedOn, setPurchasedOn] = useState('');

  const purchase = useMutation({
    mutationFn: () => createCashPayPurchase(patientId, { productId, paymentMethod, purchasedOn }),
    onSuccess: () => {
      setPurchasedOn('');
      void queryClient.invalidateQueries({ queryKey: purchasesQueryKey });
    },
  });

  const [redeemAmounts, setRedeemAmounts] = useState<Record<string, string>>({});
  const redeem = useMutation({
    mutationFn: (input: { purchaseId: string; units: string }) =>
      redeemCashPayUnits(patientId, input.purchaseId, { units: input.units }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: purchasesQueryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('cashPay.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('cashPay.description')}</p>
      <Panel title={t('cashPay.catalogPanelTitle')}>
        <QuerySection
          query={productsQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('cashPay.catalogEmptyMessage')}
        >
          {(products) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {products.map((product) => (
                <li key={product.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
                    {product.name} &mdash; ${(product.priceCents / 100).toFixed(2)}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {t(`vocab.cashPayProductTypes.${product.productType}`)}
                    {product.unitsIncluded !== null
                      ? ` ${t('cashPay.unitsIncludedSuffix', { units: product.unitsIncluded })}`
                      : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayManageBilling ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              createProduct.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('cashPay.newProductNameLabel')}
              value={newProductName}
              onChange={(event) => {
                setNewProductName(event.target.value);
              }}
              required
              className="sm:max-w-[20ch]"
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('cashPay.typeLabel')}</span>
              <select
                value={newProductType}
                onChange={(event) => {
                  setNewProductType(event.target.value as (typeof CASH_PAY_PRODUCT_TYPES)[number]);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {CASH_PAY_PRODUCT_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {t(`vocab.cashPayProductTypes.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('cashPay.priceLabel')}
              type="number"
              min={0}
              value={newPriceDollars}
              onChange={(event) => {
                setNewPriceDollars(event.target.value);
              }}
              required
              className="sm:max-w-[10ch]"
            />
            {tracksUnitBalance(newProductType) ? (
              <Field
                label={t('cashPay.unitsIncludedLabel')}
                value={newUnitsIncluded}
                onChange={(event) => {
                  setNewUnitsIncluded(event.target.value);
                }}
                placeholder={t('cashPay.unitsIncludedPlaceholder')}
                className="sm:max-w-[10ch]"
              />
            ) : null}
            <Button type="submit" pending={createProduct.isPending}>
              {t('cashPay.addProductButton')}
            </Button>
          </form>
        ) : null}
      </Panel>

      <Panel title={t('cashPay.purchasesPanelTitle')}>
        <QuerySection
          query={purchasesQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('cashPay.purchasesEmptyMessage')}
        >
          {(purchases) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {purchases.map((row) => (
                <li key={row.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
                    {row.productName} &mdash; ${(row.priceCentsPaid / 100).toFixed(2)}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {row.purchasedOn} — {row.status}
                    {row.unitsRemaining !== null
                      ? ` ${t('cashPay.unitsRemainingSuffix', { units: row.unitsRemaining })}`
                      : ''}
                  </p>
                  {mayManageBilling && row.unitsRemaining !== null ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        const amount = redeemAmounts[row.id] ?? '';
                        if (amount !== '') redeem.mutate({ purchaseId: row.id, units: amount });
                      }}
                      className="mt-2 flex items-end gap-2"
                    >
                      <Field
                        label={t('cashPay.redeemUnitsLabel')}
                        value={redeemAmounts[row.id] ?? ''}
                        onChange={(event) => {
                          setRedeemAmounts((prev) => ({ ...prev, [row.id]: event.target.value }));
                        }}
                        className="max-w-[10ch]"
                      />
                      <Button type="submit" variant="secondary" pending={redeem.isPending}>
                        {t('cashPay.redeemButton')}
                      </Button>
                    </form>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>
        {redeem.isError ? <ErrorText message={t('cashPay.redeemError')} /> : null}

        {mayManageBilling ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              purchase.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('cashPay.purchaseProductLabel')}
              </span>
              <select
                value={productId}
                onChange={(event) => {
                  setProductId(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                <option value="">{t('common.select')}</option>
                {(productsQuery.data ?? []).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('cashPay.paymentMethodLabel')}
              </span>
              <select
                value={paymentMethod}
                onChange={(event) => {
                  setPaymentMethod(event.target.value as (typeof PAYMENT_METHODS)[number]);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {PAYMENT_METHODS.map((value) => (
                  <option key={value} value={value}>
                    {t(`vocab.paymentMethods.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('cashPay.purchasedOnLabel')}
              type="date"
              value={purchasedOn}
              onChange={(event) => {
                setPurchasedOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={purchase.isPending} disabled={productId === ''}>
              {t('cashPay.recordPurchaseButton')}
            </Button>
          </form>
        ) : null}
        {purchase.isError ? <ErrorText message={t('cashPay.purchaseError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Retail POS -------------------------------------------------------------------------------------

function RetailSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('aesthetics');
  const grants = useGrants();
  const mayManageBilling = decide(grants, 'billing:manage');
  const queryClient = useQueryClient();
  const queryKey = ['aesthetics', 'retail-sales', patientId];

  const query = useQuery<readonly RetailSaleResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchRetailSales(patientId, signal),
  });

  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPriceDollars, setUnitPriceDollars] = useState('');
  const [paymentMethod, setPaymentMethod] =
    useState<(typeof RETAIL_PAYMENT_METHODS)[number]>('card');
  const [soldOn, setSoldOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createRetailSale(patientId, {
        productName,
        quantity: Number(quantity),
        unitPriceCents: Math.round(Number(unitPriceDollars) * 100),
        paymentMethod,
        soldOn,
      }),
    onSuccess: () => {
      setProductName('');
      setQuantity('1');
      setUnitPriceDollars('');
      setSoldOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('retail.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('retail.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('retail.emptyMessage')}
        >
          {(sales) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {sales.map((sale) => (
                <li key={sale.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
                    {sale.productName} &times; {sale.quantity} &mdash; $
                    {(sale.totalPriceCents / 100).toFixed(2)}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {sale.soldOn} — {t(`vocab.paymentMethods.${sale.paymentMethod}`)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayManageBilling ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('retail.productLabel')}
              value={productName}
              onChange={(event) => {
                setProductName(event.target.value);
              }}
              required
              className="sm:max-w-[20ch]"
            />
            <Field
              label={t('retail.quantityLabel')}
              type="number"
              min={1}
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
              required
              className="sm:max-w-[8ch]"
            />
            <Field
              label={t('retail.unitPriceLabel')}
              type="number"
              min={0}
              value={unitPriceDollars}
              onChange={(event) => {
                setUnitPriceDollars(event.target.value);
              }}
              required
              className="sm:max-w-[10ch]"
            />
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('retail.paymentMethodLabel')}</span>
              <select
                value={paymentMethod}
                onChange={(event) => {
                  setPaymentMethod(event.target.value as (typeof RETAIL_PAYMENT_METHODS)[number]);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {RETAIL_PAYMENT_METHODS.map((value) => (
                  <option key={value} value={value}>
                    {t(`vocab.paymentMethods.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('retail.soldOnLabel')}
              type="date"
              value={soldOn}
              onChange={(event) => {
                setSoldOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('retail.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('retail.createError')} /> : null}
      </Panel>
    </section>
  );
}
